import { useMemo, useState } from "react";
import { ArrowSquareOut, MagnifyingGlass, CaretUp, CaretDown, Plus } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";

export const STATUS = {
  active: { label: "Active", dot: "bg-emerald-500 animate-pulse" },
  inactive: { label: "Inactive", dot: "bg-slate-400" },
  trial: { label: "Trial", dot: "bg-amber-500" },
  needs_verification: { label: "Needs Verification", dot: "bg-amber-500" },
  previously_utilized: { label: "Previously utilized", dot: "bg-slate-500" },
  unknown: { label: "Unknown", dot: "bg-slate-300 border border-slate-400" },
};
export const SOURCE = { api: "API", manual: "Manual", config: "Config-detected", unverified: "Unverified" };
export const money = (p) => {
  if (p.billing_period === "free") return "$0";
  if (p.billing_period === "annual" && p.annual_cost != null) return `$${p.annual_cost.toFixed(2)}/yr (≈$${(p.annual_cost / 12).toFixed(2)}/mo)`;
  return p.monthly_cost == null ? "Unknown" : `$${p.monthly_cost.toFixed(2)}${p.billing_period === "usage" ? " (usage)" : ""}`;
};
const ago = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export function StatusDot({ s }) {
  const st = STATUS[s] || STATUS.unknown;
  return <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><span className={`w-2 h-2 rounded-full ${st.dot}`} />{st.label}</span>;
}

const COLS = [
  ["name", "Software / Provider"], ["purpose", "Purpose / What it provides"], ["plan", "Plan / Tier"], ["usage_text", "Usage / Credits"],
  ["monthly_cost", "Monthly cost (USD)"], ["card_label", "Billing card"], ["status", "Status"], ["manage_url", "Manage"], ["updated_at", "Last verified / updated"],
];

export function ConnectionsTable({ data, onOpen, onAdd }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState(["name", 1]);
  const rows = useMemo(() => {
    const t = q.toLowerCase();
    return (data?.providers || [])
      .filter((p) => filter === "all" || (filter === "active" ? p.status === "active" : p.status !== "active"))
      .filter((p) => !t || `${p.name} ${p.purpose} ${p.plan}`.toLowerCase().includes(t))
      .sort((a, b) => {
        const [k, dir] = sort; const x = a[k]; const y = b[k];
        if (x == null || x === "") return y == null || y === "" ? 0 : 1;
        if (y == null || y === "") return -1;
        return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y))) * dir;
      });
  }, [data, q, filter, sort]);
  const s = data?.summary;
  return (
    <section className="rounded-xl border border-slate-200 bg-white" data-testid="connections-table-card">
      <div className="p-3 flex items-center justify-between gap-3 flex-wrap border-b border-slate-100">
        <div className="text-xs text-slate-600 space-x-3" data-testid="connections-summary">
          <span>Confirmed recurring: <b className="text-slate-900">${(s?.confirmed_monthly ?? 0).toFixed(2)}/mo</b>{s?.annual_monthly_equivalent ? ` + $${s.annual_monthly_equivalent.toFixed(2)}/mo annualized` : ""}</span>
          <span className="text-amber-600">Estimated / unverified: ${(s?.unverified_monthly ?? 0).toFixed(2)}/mo</span>
          {s?.unknown_cost?.length > 0 && <span className="text-slate-400" title={s.unknown_cost.join(", ")}>{s.unknown_cost.length} with unknown cost</span>}
        </div>
        <div className="flex items-center gap-2">
          {["all", "active", "inactive"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`connections-filter-${f}`}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border capitalize transition-colors ${filter === f ? "bg-[#327de3] text-white border-[#327de3]" : "border-slate-200 text-slate-600 hover:border-[#327de3]"}`}>{f === "inactive" ? "Not active" : f}</button>
          ))}
          <div className="relative"><MagnifyingGlass size={13} className="absolute left-2 top-2.5 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="h-8 pl-7 w-44" data-testid="connections-search" /></div>
          <button onClick={onAdd} className="h-8 px-2.5 rounded-md border border-slate-200 text-xs font-semibold text-slate-600 hover:border-[#327de3] inline-flex items-center gap-1" data-testid="connections-add"><Plus size={12} /> Add</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="connections-table">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-400">
            {COLS.map(([k, l]) => (
              <th key={k} className="px-3 py-2 whitespace-nowrap cursor-pointer select-none" onClick={() => setSort([k, sort[0] === k ? -sort[1] : 1])} data-testid={`connections-sort-${k}`}>
                {l}{sort[0] === k && (sort[1] > 0 ? <CaretUp size={9} className="inline ml-0.5" /> : <CaretDown size={9} className="inline ml-0.5" />)}
              </th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} onClick={() => onOpen(p)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer align-top transition-colors" data-testid={`connection-row-${p.id}`}>
                <td className="px-3 py-2 font-semibold text-slate-900 whitespace-nowrap">{p.name}</td>
                <td className="px-3 py-2 text-slate-600 max-w-[340px]"><div className="line-clamp-2">{p.purpose}</div></td>
                <td className="px-3 py-2 text-slate-700 min-w-[170px] max-w-[240px]">{p.plan || "—"}</td>
                <td className="px-3 py-2 text-slate-500 max-w-[200px]">{p.usage_text || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap"><div className="font-semibold text-slate-900 tabular-nums">{money(p)}</div><div className="text-[10px] text-slate-400">{SOURCE[p.cost_source] || "Unverified"}</div></td>
                <td className="px-3 py-2 whitespace-nowrap text-slate-700">{p.card_label ? <>{p.card_label}{!p.card_verified && <div className="text-[10px] text-amber-600">pending verification</div>}</> : "—"}</td>
                <td className="px-3 py-2"><StatusDot s={p.status} /></td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {p.manage_url ? <a href={p.manage_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#327de3] font-semibold inline-flex items-center gap-1 hover:underline" data-testid={`connection-manage-${p.id}`}>Open <ArrowSquareOut size={11} /></a> : "—"}
                  {p.link_checked_at && <div className={`text-[10px] ${p.link_status && p.link_status < 400 ? "text-emerald-600" : "text-rose-500"}`}>{p.link_status && p.link_status < 400 ? "reachable" : p.link_status === 404 ? "HTTP 404 (sign-in page?)" : p.link_status ? `HTTP ${p.link_status}` : "no response"}</div>}
                </td>
                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{ago(p.last_verified_at || p.updated_at)}<div className="text-[10px] text-slate-400">{p.last_verified_at ? "verified" : "updated"}</div></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400" data-testid="connections-empty">No connections match.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
